import { Swiper, SwiperSlide } from 'swiper/react'
import type { Swiper as SwiperType } from 'swiper'
import { Autoplay, Pagination, Navigation, EffectFade } from 'swiper/modules'
import 'swiper/css'
import 'swiper/css/pagination'
import 'swiper/css/navigation'
import 'swiper/css/effect-fade'
import { ChevronLeft, ChevronRight, Loader } from 'lucide-react'
import { useState, useEffect } from 'react'
import OptimizedImage from './OptimizedImage'
import { homepageService } from '../lib/homepageService'
import type { HomepageSlide } from '../types/homepage'

// Default slides as fallback
const defaultSlides: HomepageSlide[] = [
  {
    id: 'slide-default-1',
    imageUrl: 'https://images.unsplash.com/photo-1523580494863-6f3031224c94?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
    title: 'Conectando Brasileiros nos EUA',
    description: 'Mais de 1 milhão de pessoas já foram ajudadas pelo Grupo Noé em sua jornada no exterior.',
    ctaText: 'Saiba mais',
    ctaLink: '/about',
    order: 0,
    active: true,
    createdAt: new Date('2026-04-01'),
    updatedAt: new Date('2026-04-01'),
  },
  {
    id: 'slide-default-2',
    imageUrl: 'https://images.unsplash.com/photo-1559136555-9303baea8ebd?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
    title: 'Comunidade que Acolhe',
    description: 'Oferecemos suporte, orientação e oportunidades para brasileiros que buscam uma nova vida nos Estados Unidos.',
    ctaText: 'Junte-se a nós',
    ctaLink: '/register',
    order: 1,
    active: true,
    createdAt: new Date('2026-04-01'),
    updatedAt: new Date('2026-04-01'),
  },
  {
    id: 'slide-default-3',
    imageUrl: 'https://images.unsplash.com/photo-1542744173-8e7e53415bb0?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
    title: 'Tempo de Experiência',
    description: 'Há mais de 10 anos ajudando pessoas a se estabelecerem, encontrar emprego, moradia e construir redes de contato.',
    ctaText: 'Nossa história',
    ctaLink: '/about',
    order: 2,
    active: true,
    createdAt: new Date('2026-04-01'),
    updatedAt: new Date('2026-04-01'),
  },
  {
    id: 'slide-default-4',
    imageUrl: 'https://images.unsplash.com/photo-1573164713988-8665fc963095?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
    title: 'Marketplace de Oportunidades',
    description: 'Encontre imóveis, carros, serviços e muito mais em nossa plataforma exclusiva para a comunidade brasileira.',
    ctaText: 'Explorar anúncios',
    ctaLink: '/listings',
    order: 3,
    active: true,
    createdAt: new Date('2026-04-01'),
    updatedAt: new Date('2026-04-01'),
  },
]

const HeroCarousel = () => {
  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null)
  const [slides, setSlides] = useState<HomepageSlide[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const loadSlides = async () => {
      try {
        const slidesData = await homepageService.getSlides()
        // Filter active slides and sort by order
        const activeSlides = slidesData
          .filter(slide => slide.active)
          .sort((a, b) => a.order - b.order)
        
        setSlides(activeSlides.length > 0 ? activeSlides : defaultSlides)
      } catch (error) {
        console.error('Error loading slides:', error)
        setSlides(defaultSlides)
      } finally {
        setLoading(false)
      }
    }
    
    loadSlides()
  }, [])

  if (loading) {
    return (
      <div className="relative w-full h-[500px] md:h-[600px] rounded-2xl overflow-hidden shadow-2xl bg-noe-900/30 border border-noe-800 flex items-center justify-center">
        <Loader className="w-12 h-12 text-noe-brand animate-spin" />
      </div>
    )
  }

  return (
    <div className="relative w-full h-[500px] md:h-[600px] rounded-2xl overflow-hidden shadow-2xl">
      <Swiper
        onSwiper={setSwiperInstance}
        modules={[Autoplay, Pagination, Navigation, EffectFade]}
        effect="fade"
        fadeEffect={{ crossFade: true }}
        speed={800}
        autoplay={{ delay: 5000, disableOnInteraction: false }}
        pagination={{ clickable: true, dynamicBullets: true }}
        navigation={{
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        }}
        loop={true}
        className="w-full h-full"
      >
        {slides.map((slide, index) => (
          <SwiperSlide key={index}>
            <div className="relative w-full h-full">
              <OptimizedImage
                src={slide.imageUrl}
                alt={slide.title}
                className="absolute inset-0 w-full h-full object-cover"
                width={1920}
                height={600}
                fallbackSrc="https://images.unsplash.com/photo-1559136555-9303baea8ebd?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/50 to-transparent" />
              <div className="absolute inset-0 flex items-center">
                <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 w-full">
                  <div className="max-w-2xl">
                     <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-noe-brand mb-4 leading-tight drop-shadow-lg">
                       {slide.title}
                     </h1>
                      <p className="text-lg md:text-xl text-white mb-8 drop-shadow-lg">
                        {slide.description}
                      </p>
                    <button 
                      onClick={() => window.location.href = slide.ctaLink}
                      className="px-8 py-3 bg-noe-brand text-noe-950 font-bold rounded-lg hover:bg-noe-brand/90 transition-colors text-lg shadow-lg cursor-pointer"
                    >
                      {slide.ctaText}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </SwiperSlide>
        ))}
      </Swiper>
      
       {/* Custom Navigation Buttons */}
       <button
         className="swiper-button-prev absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-black/70 backdrop-blur-sm rounded-full hover:bg-noe-brand/30 border-none transition-all duration-300 group"
         onClick={() => swiperInstance?.slidePrev()}
         aria-label="Slide anterior"
       >
         <ChevronLeft className="w-6 h-6 text-noe-brand group-hover:scale-110 transition-transform" />
       </button>
       <button
         className="swiper-button-next absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-black/70 backdrop-blur-sm rounded-full hover:bg-noe-brand/30 border-none transition-all duration-300 group"
         onClick={() => swiperInstance?.slideNext()}
         aria-label="Próximo slide"
       >
         <ChevronRight className="w-6 h-6 text-noe-brand group-hover:scale-110 transition-transform" />
       </button>
      
      {/* Overlay gradient at bottom */}
       <div className="absolute bottom-0 left-0 right-0 h-32 bg-gradient-to-t from-black to-transparent pointer-events-none" />
    </div>
  )
}

export default HeroCarousel